import React from "react";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import { CircularProgress } from "@mui/material";

interface BayHireFileCardProps {
  fileName: string;
  /** Optional caption under the file name (e.g. the attachment type). */
  caption?: string | null;
  isDownloading: boolean;
  /** Opens the document in a new tab (view) - the file name's own control. */
  onView: () => void;
  /** Saves the document to disk (download) - the trailing icon's own control. */
  onDownload: () => void;
  testId: string;
}

/**
 * DFT-0027: Figma p10/p33 file card, revised by DFT-0050 (AC29): the file name and the
 * download icon are now two INDEPENDENT controls - opening the document (view, in a new tab)
 * and saving it to disk (download) are different actions with different consequences, and a
 * single control doing both silently downloaded a file when the user only wanted to look at it.
 * Matches `LicenseCard.tsx`'s existing view-link/download-icon split
 * (`frontend-guidelines.md#L143-L157`). Rendered as buttons rather than anchors because there is
 * no directly linkable URL: the bytes are fetched via a SAS token and handed to the browser as an
 * object URL (DFT-0050 AD1/AD3, `useDownloadBayHireAttachment`).
 */
const BayHireFileCard: React.FC<BayHireFileCardProps> = ({
  fileName,
  caption,
  isDownloading,
  onView,
  onDownload,
  testId,
}) => (
  <div
    className="flex items-center justify-between gap-3 border border-gray-200 rounded-md px-4 py-3 bg-white"
    data-testid={`${testId}-card`}
  >
    <div className="flex items-center gap-3 min-w-0">
      <InsertDriveFileOutlinedIcon fontSize="small" className="text-gray-400 shrink-0" />
      <div className="min-w-0">
        <button
          type="button"
          onClick={onView}
          disabled={isDownloading}
          data-testid={testId}
          className="text-sm text-blue-600 hover:underline truncate disabled:text-gray-400 disabled:no-underline disabled:cursor-not-allowed"
        >
          {fileName}
        </button>
        {caption ? <p className="text-xs text-gray-500 truncate">{caption}</p> : null}
      </div>
    </div>
    {isDownloading ? (
      <CircularProgress size={16} data-testid={`${testId}-spinner`} />
    ) : (
      <button
        type="button"
        onClick={onDownload}
        data-testid={`${testId}-download`}
        aria-label="Download"
        className="text-gray-400 hover:text-gray-600 shrink-0"
      >
        <DownloadOutlinedIcon fontSize="small" />
      </button>
    )}
  </div>
);

export default BayHireFileCard;
